import { Property } from 'csstype'
import _ from 'lodash'
import moment, { Moment } from 'moment'
import React from 'react'

import { FLAG_COLOURS } from 'constants/colors'
import { BasicViewProps } from 'constants/models'
import { PaginationDetails } from 'constants/pagination'
import {
  MEDICATION_FORM_STATUSES,
  MEDICATION_FORM_STATUS_OPTIONS,
  MedicationFormStatusOptionProps,
  STORED_ON_SITE,
  STORED_ON_SITE_OPTIONS,
} from 'services/medicationForms/constants'

import { generateRoute } from 'utils/routing'
import { createTypeFromEnumValues } from 'utils/typescript'

import {
  Callout,
  DatePicker,
  EmptyState,
  Form,
  InfiniteDropdowns,
  Page,
  Pagination,
  SearchBar,
  Section,
  Select,
  Space,
  Spinner,
  Table,
  Toolbar,
} from 'components'
import { PaginationProps } from 'components/Pagination'
import { TableColumn } from 'components/Table'

import i18n from 'translations'

import { MedicationFormTableData } from './ListHelpers'
import { StyledTableWrapper } from './ListStyled'

interface ListViewProps extends BasicViewProps, PaginationProps {
  childId: number
  dateRange: Moment[]
  isChildContext: boolean
  medicationFormListTableData: MedicationFormTableData[]
  medicationFormStatus?: MedicationFormStatusOptionProps
  onDateRangeChange?: (date: moment.Moment[]) => void
  onMedicationFormStatusChange?: (status: MedicationFormStatusOptionProps) => void
  onRoomChange: (value: string) => void
  onSearchChange: (value: string) => void
  onStoredOnSiteChange?: (value: createTypeFromEnumValues<STORED_ON_SITE>) => void
  pagination: PaginationDetails
  room?: number
  search?: string
  storedOnSite?: createTypeFromEnumValues<STORED_ON_SITE>
  tableColumns: TableColumn[]
}

const ListView: React.FC<ListViewProps> = ({
  childId,
  dateRange,
  errorMessages,
  isChildContext,
  isLoading,
  medicationFormListTableData,
  medicationFormStatus,
  onDateRangeChange,
  onMedicationFormStatusChange,
  onRoomChange,
  onSearchChange,
  onStoredOnSiteChange,
  pagination,
  room,
  search,
  storedOnSite,
  tableColumns,
}) => {
  const renderPagination = () => !isLoading && (
    <Pagination {...pagination} />
  )

  const renderContent = () => {
    if (isLoading) {
      return <Spinner />
    }

    if (!medicationFormListTableData?.length) {
      return (
        <EmptyState
          icon="medication"
          text1={isChildContext
            ? i18n.t('module:Safeguarding:MedicationForm:List:emptyStateChildContext')
            : i18n.t('module:Safeguarding:MedicationForm:List:emptyStateText1')}
        />
      )
    }

    return (
      <React.Fragment>
        {renderPagination()}
        <StyledTableWrapper>
          <Table visualType="flat">
            <Table.Thead>
              {_.map(tableColumns, ({ alignTh, field, title }) => (
                <Table.Th align={alignTh as Property.TextAlign} key={field}>{title}</Table.Th>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {_.map(medicationFormListTableData, (item) => {
                const isMedicationExpired = item.statusValue === MEDICATION_FORM_STATUSES.EXPIRED
                const remainingExpirationWeeks = moment(item.expiryDateValue)
                  .startOf('day')
                  .diff(moment().startOf('day'), 'weeks', true)
                const isMedicationExpiring = item.statusValue === MEDICATION_FORM_STATUSES.ACTIVE
                  && 2 >= remainingExpirationWeeks

                return (
                  <Table.Tr background={isMedicationExpired ? `${FLAG_COLOURS.ERROR}1A` : undefined} key={item.id}>
                    {_.map(tableColumns, ({ align, field, width }) => (
                      <Table.Td
                        align={align as Property.TextAlign}
                        color={((isMedicationExpired || isMedicationExpiring) && 'expiryDate' === field)
                          ? FLAG_COLOURS.ERROR
                          : undefined}
                        key={`${item.id}-${field}`}
                        width={width}
                      >
                        {item[field]}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </StyledTableWrapper>
        {renderPagination()}
      </React.Fragment>
    )
  }

  const renderFilters = () => (
    <React.Fragment>
      <SearchBar
        placeholder={i18n.t('module:Safeguarding:MedicationForm:Filters:search')}
        value={search}
        variant="standard"
        noBackground
        onChange={onSearchChange}
      />
      <Space margin="0 0 10px" />
      <Toolbar>
        <Toolbar.Group>
          {!isChildContext && (
            <Toolbar.Item>
              <Form.Row label={i18n.t('global:Room')} verticalLabel>
                <InfiniteDropdowns.Rooms placeholder={i18n.t('global:AllRooms')} value={room} onChange={onRoomChange} />
              </Form.Row>
            </Toolbar.Item>
          )}
          <Toolbar.Item>
            <Form.Row label={i18n.t('global:Status')} verticalLabel>
              <Select
                options={MEDICATION_FORM_STATUS_OPTIONS}
                placeholder={i18n.t('global:All')}
                value={medicationFormStatus}
                onChange={onMedicationFormStatusChange}
              />
            </Form.Row>
          </Toolbar.Item>
          <Toolbar.Item>
            <Form.Row label={i18n.t('module:Safeguarding:MedicationForm:Filters:storeOnSite')} verticalLabel>
              <Select
                options={STORED_ON_SITE_OPTIONS}
                placeholder={i18n.t('global:All')}
                value={storedOnSite}
                onChange={onStoredOnSiteChange}
              />
            </Form.Row>
          </Toolbar.Item>
          <Toolbar.Item>
            <Form.Row label={i18n.t('global:DateRange')} verticalLabel>
              <DatePicker value={dateRange} clearable range onChange={onDateRangeChange} />
            </Form.Row>
          </Toolbar.Item>
        </Toolbar.Group>
      </Toolbar>
    </React.Fragment>
  )

  return (
    <Page.Section
      actions={(
        <Section.Actions
          primary={[{
            to: isChildContext
              ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.ADD', { childId })
              : generateRoute('SAFEGUARDING.MEDICATION_FORM.ADD'),
          }]}
        />
      )}
      title={i18n.t('module:Safeguarding:MedicationForm:List:title')}
    >
      <Callout content={errorMessages} error />
      {renderFilters()}
      {renderContent()}
    </Page.Section>
  )
}

export default ListView
